import React from 'react';
import { SidebarSectionConfig } from './sidebar-config';
import { MorePopoverSidebarItem } from './MorePopoverSidebarItem';
import { SidebarHoverPopover } from './SidebarHoverPopover';
import { SidebarItem } from './SidebarItem';

interface SidebarSectionProps {
  section: SidebarSectionConfig;
  isCollapsed: boolean;
  onItemClick?: (itemId: string) => void;
  itemCallbacks?: Record<string, () => void>; // Custom callbacks for specific items
  /** When true, show label below icon (mini sidebar style, no tooltip, no badge) */
  showLabelBelow?: boolean;
  /** Optional map of hover content for mini view (key = hoverContentKey from item config). */
  hoverContentMap?: Record<string, React.ReactNode>;
}

/**
 * Reusable sidebar section component
 * Renders a section with title and items
 */
export const SidebarSection: React.FC<SidebarSectionProps> = ({
  section,
  isCollapsed,
  onItemClick,
  itemCallbacks,
  showLabelBelow = false,
  hoverContentMap,
}) => {
  // Filter out hidden items
  const visibleItems = section.items.filter((item) => {
    if (item.isHidden) {
      return !item.isHidden();
    }
    return true;
  });

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className={`${section.className || ''}`}>
      {/* Section title - show when expanded or in mini sidebar with labels (e.g. "More") */}
      {section.title && (!isCollapsed || showLabelBelow) && (
        <h3
          className={`text-gray-500 dark:text-gray-400 ${
            showLabelBelow
              ? 'mt-1 mb-1 text-center text-xs font-semibold tracking-wider uppercase first:mt-0'
              : 'mb-2 px-2 text-xs font-semibold tracking-wider uppercase'
          }`}
        >
          {section.title}
        </h3>
      )}

      {/* Section items */}
      <div
        className={`${isCollapsed && !showLabelBelow ? 'space-y-1' : 'space-y-0'} ${isCollapsed && showLabelBelow ? 'space-y-1.5' : ''}`}
      >
        {visibleItems.map((item) => {
          // "More" is a single item that opens a popover with Chat with docs, Feature request, Contact support
          if (item.id === 'more') {
            return (
              <div
                key={item.id}
                className={
                  isCollapsed && !showLabelBelow ? 'flex justify-center' : ''
                }
              >
                <MorePopoverSidebarItem
                  item={item}
                  isCollapsed={isCollapsed}
                  showLabelBelow={showLabelBelow}
                  itemCallbacks={itemCallbacks}
                  onItemClick={onItemClick}
                />
              </div>
            );
          }

          const callback = itemCallbacks?.[item.id];
          const sidebarItem = (
            <SidebarItem
              item={{
                ...item,
                onClick: callback || item.onClick,
              }}
              isCollapsed={isCollapsed}
              onClick={() => onItemClick?.(item.id)}
              showLabelBelow={showLabelBelow}
            />
          );
          const wrapperClass =
            isCollapsed && !showLabelBelow ? 'flex justify-center' : '';
          const hasHoverContent =
            showLabelBelow &&
            item.hoverContentKey &&
            hoverContentMap &&
            item.hoverContentKey in hoverContentMap;

          return (
            <div key={item.id} className={wrapperClass}>
              {hasHoverContent && hoverContentMap && item.hoverContentKey ? (
                <SidebarHoverPopover
                  title={item.hoverTitle ?? item.label}
                  content={hoverContentMap[item.hoverContentKey]}
                  preferredSide="right"
                >
                  {sidebarItem}
                </SidebarHoverPopover>
              ) : (
                sidebarItem
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
